import { z } from 'zod';
import { zObject } from '../../util/zod';
import { WidgetsName } from './common';
import { widgetArticleAttachmentContentSchema, widgetArticleAttachmentTypeSchema } from './widgetArticleAttachment';
import { widgetArticleButtonContentSchema, widgetArticleButtonTypeSchema } from './widgetArticleButton';
import { widgetArticleGalleryContentSchema, widgetArticleGalleryTypeSchema } from './widgetArticleGallery';
import { widgetArticleImageContentSchema, widgetArticleImageTypeSchema } from './widgetArticleImage';
import { widgetArticleSliderContentSchema, widgetArticleSliderTypeSchema } from './widgetArticleSlider';
import { widgetArticleTextContentSchema, widgetArticleTextTypeSchema } from './widgetArticleText';
import { widgetArticleTitleContentSchema, widgetArticleTitleTypeSchema } from './widgetArticleTitle';
import { widgetCheckListContentSchema, widgetCheckListTypeSchema } from './widgetCheckList';
import { widgetDatasourceTableContentSchema, widgetDatasourceTableTypeSchema } from './widgetDatasourceTable';
import {
    widgetExcelDatasourceTableContentSchema,
    widgetExcelDatasourceTableTypeSchema,
} from './widgetExcelDatasourceTable';
import { widgetPresenceContentSchema, widgetPresenceTypeSchema } from './widgetPresence';
import { widgetQuickSurveyContentSchema, widgetQuickSurveyTypeSchema } from './widgetQuickSurvey';

import { widgetCalendarCalDavContentSchema, widgetCalendarCalDavTypeSchema } from './widgetCalendarCalDav';
import { widgetContactCardDavContentSchema, widgetContactCardDavTypeSchema } from './widgetContactCardDav';
import { widgetEmailsImapContentSchema, widgetEmailsImapTypeSchema } from './widgetEmailsImap';
import { widgetLuccaAbsenceContentSchema, widgetLuccaAbsenceTypeSchema } from './widgetLuccaAbsence';

export const combinedWidgetTypeSchema = z.union([
    z.object({ layers: z.string().array().optional() }).and(widgetArticleAttachmentTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetArticleButtonTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetArticleGalleryTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetArticleImageTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetArticleTitleTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetArticleTextTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetArticleSliderTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetCheckListTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetDatasourceTableTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetExcelDatasourceTableTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetPresenceTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetQuickSurveyTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetCalendarCalDavTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetContactCardDavTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetEmailsImapTypeSchema),
    z.object({ layers: z.string().array().optional() }).and(widgetLuccaAbsenceTypeSchema),
]);

export type CombinedWidgetType = z.infer<typeof combinedWidgetTypeSchema>;

export const WIDGET_LEVEL_1 = 1;
export const WIDGET_LEVEL_2 = 2;
export const WIDGET_LEVEL_3 = 3;
export const WIDGET_LEVEL_4 = 4;

export const widgetWrapperLevelSchema = z.union([
    z.literal(WIDGET_LEVEL_1),
    z.literal(WIDGET_LEVEL_2),
    z.literal(WIDGET_LEVEL_3),
    z.literal(WIDGET_LEVEL_4),
]);
export type WidgetWrapperLevel = z.infer<typeof widgetWrapperLevelSchema>;

export type WidgetWrapperGeneric<T = CombinedWidgetType> = {
    title: string;
    level?: WidgetWrapperLevel;
    widget: T;
};
export const widgetWrapperPropsSchema = zObject<WidgetWrapperGeneric>({
    title: z.string(),
    level: widgetWrapperLevelSchema.optional(),
    widget: combinedWidgetTypeSchema,
});
export type WidgetWrapperProps = z.infer<typeof widgetWrapperPropsSchema>;

export const combinedWidgetContentSchema = z
    .object({
        widgets: widgetWrapperPropsSchema.array().optional(),
    })
    .and(
        z.union([
            widgetArticleAttachmentContentSchema,
            widgetArticleButtonContentSchema,
            widgetArticleGalleryContentSchema,
            widgetArticleImageContentSchema,
            widgetArticleTextContentSchema,
            widgetArticleTitleContentSchema,
            widgetArticleSliderContentSchema,
            widgetCheckListContentSchema,
            widgetDatasourceTableContentSchema,
            widgetExcelDatasourceTableContentSchema,
            widgetPresenceContentSchema,
            widgetQuickSurveyContentSchema,
            widgetCalendarCalDavContentSchema,
            widgetContactCardDavContentSchema,
            widgetEmailsImapContentSchema,
            widgetLuccaAbsenceContentSchema,
        ]),
    );

export type CombinedWidgetContent = z.infer<typeof combinedWidgetContentSchema>;

export const widgetTypeKeysSchema = z.object({
    [WidgetsName.ArticleButton]: widgetArticleButtonTypeSchema,
    [WidgetsName.ArticleGallery]: widgetArticleGalleryTypeSchema,
    [WidgetsName.ArticleTitle]: widgetArticleTitleTypeSchema,
    [WidgetsName.ArticleText]: widgetArticleTextTypeSchema,
    [WidgetsName.ArticleImage]: widgetArticleImageTypeSchema,
    [WidgetsName.ArticleAttachment]: widgetArticleAttachmentTypeSchema,
    [WidgetsName.ArticleSlider]: widgetArticleSliderTypeSchema,
    [WidgetsName.CheckList]: widgetCheckListTypeSchema,
    [WidgetsName.DatasourceTable]: widgetDatasourceTableTypeSchema,
    [WidgetsName.ExcelDatasourceTable]: widgetExcelDatasourceTableTypeSchema,
    [WidgetsName.Presence]: widgetPresenceTypeSchema,
    [WidgetsName.QuickSurvey]: widgetQuickSurveyTypeSchema,
    [WidgetsName.CalendarCalDav]: widgetCalendarCalDavTypeSchema,
    [WidgetsName.ContactCardDav]: widgetContactCardDavTypeSchema,
    [WidgetsName.EmailsImap]: widgetEmailsImapTypeSchema,
    [WidgetsName.LuccaAbsence]: widgetLuccaAbsenceTypeSchema,
});
export type WidgetTypeKeys = z.infer<typeof widgetTypeKeysSchema>;

export const widgetContentKeysSchema = z.object({
    [WidgetsName.ArticleButton]: widgetArticleButtonContentSchema,
    [WidgetsName.ArticleGallery]: widgetArticleGalleryContentSchema,
    [WidgetsName.ArticleTitle]: widgetArticleTitleContentSchema,
    [WidgetsName.ArticleText]: widgetArticleTextContentSchema,
    [WidgetsName.ArticleImage]: widgetArticleImageContentSchema,
    [WidgetsName.ArticleAttachment]: widgetArticleAttachmentContentSchema,
    [WidgetsName.ArticleSlider]: widgetArticleSliderContentSchema,
    [WidgetsName.CheckList]: widgetCheckListContentSchema,
    [WidgetsName.DatasourceTable]: widgetDatasourceTableContentSchema,
    [WidgetsName.ExcelDatasourceTable]: widgetExcelDatasourceTableContentSchema,
    [WidgetsName.Presence]: widgetPresenceContentSchema,
    [WidgetsName.QuickSurvey]: widgetQuickSurveyContentSchema,
    [WidgetsName.CalendarCalDav]: widgetCalendarCalDavContentSchema,
    [WidgetsName.ContactCardDav]: widgetContactCardDavContentSchema,
    [WidgetsName.EmailsImap]: widgetEmailsImapContentSchema,
    [WidgetsName.LuccaAbsence]: widgetLuccaAbsenceContentSchema,
});

export type WidgetContentKeys = z.infer<typeof widgetContentKeysSchema>;

export const widgetStatePropsSchema = z.object({
    busy: z.union([z.boolean(), z.undefined()]).optional(),
    loading: z.union([z.boolean(), z.undefined()]).optional(),
    initialized: z.union([z.boolean(), z.undefined()]).optional(),
    mounted: z.union([z.boolean(), z.undefined()]).optional(),
    hover: z.union([z.boolean(), z.undefined()]).optional(),
    empty: z.boolean().optional(),
    locked: z.boolean().optional(),
});
export type WidgetStateProps = z.infer<typeof widgetStatePropsSchema>;

export type WidgetEditorActions<T = CombinedWidgetContent> = {
    onChange: (content: Partial<T>, override?: boolean) => void;
    onSave: (content: Partial<T>) => void;
    onStateChange: (state: WidgetStateProps) => void;
};

export type WidgetEditorErrorActions = {
    onError: (message: string) => void;
};
